import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import { Property } from '@propertypal/shared/src/types/property';
import { WebsiteUser } from '@propertypal/shared/src/types/websiteUser';
import getSellingAgent from '@propertypal/shared/src/utils/property/getSellingAgent';
import WhiteModal from '@propertypal/web-ui/src/modal/WhiteModal';
import PropertyAgentBar from '@propertypal/web-ui/src/property/PropertyAgentBar';
import { MidHeading } from '@propertypal/web-ui/src/typography';
import React, { FunctionComponent, useEffect, useMemo, useRef } from 'react';
import { useTheme } from 'styled-components';
import EnquiryForm from '../my/enquiries/EnquiryForm';
import { AgentHeader } from './EnquiryModal.style';

interface Props {
  show: boolean;
  property: Property;
  onSuccess?: (leadId: number) => void;
  onClose: () => void;
  accountNumber?: string;
  registerInterestModal?: boolean;
  loggedIn: boolean;
  hideHeader?: boolean;
  loggedInUser?: WebsiteUser;
  page: string;
}

const EnquiryModal: FunctionComponent<Props> = (props) => {
  const sellingAgent = getSellingAgent(props.property);
  const closeRef = useRef<HTMLButtonElement>(null);
  const theme = useTheme();
  const showTopBanner = sellingAgent?.enhancedBranding && !props.registerInterestModal;
  const title = useMemo(() => {
    if (props.hideHeader) return undefined;
    if (props.registerInterestModal) return 'Register Your Interest';
    return 'Enquire Now';
  }, [props.hideHeader, props.registerInterestModal]);

  const onSuccess = (accountNumber: string, leadId?: number) => {
    trackGaEvent(props.registerInterestModal ? 'register_interest' : 'send_enquiry', {
      page: props.page,
      account_number: accountNumber,
    });

    if (leadId && props.onSuccess) {
      props.onSuccess(leadId);
    }
  };

  useEffect(() => {
    if (props.show) {
      closeRef.current?.focus();
    }
  }, [props.show]);

  return (
    <WhiteModal
      show={props.show}
      onClose={props.onClose}
      title={title}
      closeRef={closeRef}
      maxWidth={props.registerInterestModal ? 780 : 580}
      testID="enquiry-modal"
      contentStyle={{ position: 'relative' }}
    >
      {showTopBanner && (
        <AgentHeader>
          <PropertyAgentBar property={props.property} onClose={props.onClose} />
        </AgentHeader>
      )}

      {props.registerInterestModal && (
        <MidHeading mb={15} fontSize={18}>
          <b>By registering your interest</b> you&apos;ll join an exclusive group of home-searchers first to hear about
          the latest development updates and be ahead of the competition with the opportunity to reserve one as soon as
          they become available
        </MidHeading>
      )}

      {props.property.style?.key === 'studentApartment' && (
        <MidHeading color={theme.primary} fontSize={18} align="center" mb={30}>
          Please note these rentals are only available to students.
        </MidHeading>
      )}

      <EnquiryForm
        property={props.property}
        onSuccess={onSuccess}
        initialAccountNumber={props.accountNumber}
        registerInterestModal={props.registerInterestModal}
        loggedIn={props.loggedIn}
        loggedInUser={props.loggedInUser}
      />
    </WhiteModal>
  );
};

export default EnquiryModal;
